import React from 'react'
import {observable, observe} from 'mobx'
import {useEventBusChannel} from './EventBus'
import {SaveableStateEvents, SetDirtyEventProps} from './saveableStateEvents'

interface DirtyableState {
    isDirty: boolean
}

export function useRegisterDirtyableState(state: DirtyableState, view: string): void {
    const eventBusChannel = useEventBusChannel<SaveableStateEvents>()
    React.useEffect(() => {
        const cleanup = observe(
            state,
            'isDirty',
            change =>
                eventBusChannel.trigger('setDirty', {
                    view,
                    isDirty: !!change.newValue
                }),
            true
        )

        return () => {
            cleanup()
            eventBusChannel.trigger('setDirty', {
                view,
                isDirty: false
            })
        }
    }, [eventBusChannel, state, view])
}

export function useDirtyableState(initialValue: boolean, view: string): [boolean, (value: boolean) => void] {
    const eventBusChannel = useEventBusChannel<SaveableStateEvents>()
    const [isDirty, setDirty] = React.useState(initialValue)

    React.useEffect(() => {
        eventBusChannel.trigger('setDirty', {
            view,
            isDirty
        })
    }, [eventBusChannel, view, isDirty])

    React.useEffect(() => {
        return () => {
            eventBusChannel.trigger('setDirty', {
                view,
                isDirty: false
            })
        }
    }, [eventBusChannel, view])

    return [isDirty, setDirty]
}

const DirtyViewsContext = React.createContext(observable(new Set<string>()))

export function useGlobalDirtyViews(): Set<string> {
    const dirtyViews = React.useContext(DirtyViewsContext)

    const saveDirty = React.useCallback(
        (event: SetDirtyEventProps) => {
            if (event.isDirty) {
                dirtyViews.add(event.view)
            } else {
                dirtyViews.delete(event.view)
            }
        },
        [dirtyViews]
    )

    useEventBusChannel<SaveableStateEvents>().useEvent('setDirty', saveDirty)

    return dirtyViews
}

export function useGlobalIsDirty(): boolean {
    return useGlobalDirtyViews().size > 0
}
